import { useEffect, useMemo, useState } from 'react'
import { Loader2, ClipboardList } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { CreatableSelect } from '@/shared/components/ui/creatable-select'
import { toastApiError } from '@/shared/lib/api-error'
import { AddEvidenceDialogShell } from '@/shared/components/evidence/add-evidence-dialog-shell'
import {
  getAcceptForUseCase,
  humanizeAccept,
  type UploadedFileItem,
} from '@/shared/lib/file-types'
import { useClaimChecklist } from '@/admin/hooks/use-lab-tasks'
import {
  useClaimAttachments,
  useCreateDocumentCategory,
  useDeleteAttachment,
  useDocumentCategories,
  useUploadClaimEvidence,
} from '@/admin/pages/claims/use-claim-attachments'

// Sentinel for the "not linked" option of the checklist-item select. Radix
// Select forbids an empty-string item value, so we round-trip this to '' when
// reading the selection.
const NO_CHECKLIST_ITEM = '__none__'

/** A category the upload can be filed under: a checklist item or a photo category. */
interface EvidenceCategoryOption {
  key: string
  label: string
  satisfied: boolean
}

/** The opt-out row, offered only while a real category is not required. */
const NOT_LINKED_OPTION = (
  t: (key: string, fallback: string) => string,
): EvidenceCategoryOption => ({
  key: NO_CHECKLIST_ITEM,
  label: t('claims.notLinkedToChecklistItem', 'Not linked to a checklist item'),
  satisfied: false,
})

/**
 * Category keys that name the medium rather than the evidence. `file_type`
 * already records the medium, so one of these on its own tells a reviewer
 * nothing and satisfies no checklist item - the upload needs a real category
 * alongside it (WPS-140 #5). Tenants whose taxonomy is entirely specific never
 * hit this.
 */
const GENERIC_MEDIA_CATEGORY_KEYS = new Set([
  'photo',
  'photos',
  'image',
  'images',
  'video',
  'videos',
  'document',
  'documents',
  'file',
  'files',
  'other',
])

const ACCEPT = getAcceptForUseCase('CLAIM_EVIDENCE')

/**
 * Structured "add evidence" dialog: the user classifies the document with a
 * category (and a sub-category when the category has one), then uploads one
 * or more files. Each file uploads on its own with a live progress row and
 * lands on the claim tagged with the chosen category; a per-row trash removes
 * a just-added file via the same audited soft-delete the Evidence list uses.
 * Category taxonomy is the tenant's configurable list (useDocumentCategories)
 * - never hardcoded.
 */
export function AddEvidenceDialog({
  claimUuid,
  section,
  onClose,
}: {
  claimUuid: string
  /** When set, scope the category list to those relevant to this section. */
  section?: string
  /** Called when the dialog is dismissed. The parent unmounts on close. */
  onClose: () => void
}) {
  const {
    data: allCategories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useDocumentCategories()
  const { t } = useTranslation()
  const { data: attachments = [] } = useClaimAttachments(claimUuid)
  const { data: checklist } = useClaimChecklist(claimUuid)
  const upload = useUploadClaimEvidence(claimUuid)
  const del = useDeleteAttachment(claimUuid)
  const createCategory = useCreateDocumentCategory()

  const [categoryValue, setCategoryValue] = useState('')
  const [subcategoryKey, setSubcategoryKey] = useState('')
  // Which checklist requirement this upload fulfils (optional). Stored on the
  // file as `requirement_key`, which flips the matching Review Checklist item
  // to satisfied. Only offered when the claim resolves a checklist.
  const [checklistItemKey, setChecklistItemKey] = useState('')
  const [added, setAdded] = useState<UploadedFileItem[]>([])
  const [pendingDelete, setPendingDelete] = useState<UploadedFileItem | null>(
    null,
  )

  const categories = useMemo(
    () =>
      section
        ? allCategories.filter((c) =>
            (c.sections ?? ['overview']).includes(section),
          )
        : allCategories,
    [allCategories, section],
  )

  const categoryOptions = useMemo(() => {
    const opts = categories.map((c) => ({ value: c.key, label: c.label }))
    // Keep the selected category represented even when a section filter would
    // otherwise hide it (e.g. a just-created overview-only category picked from
    // a section-scoped dialog), so the trigger shows its label, not the raw key.
    if (categoryValue && !opts.some((o) => o.value === categoryValue)) {
      const found = allCategories.find((c) => c.key === categoryValue)
      if (found) opts.push({ value: found.key, label: found.label })
    }
    return opts
  }, [categories, allCategories, categoryValue])

  // What the claim is still waiting on, per the checklist - which is the same
  // thing the claimant is being asked for, and already resolves `satisfied`
  // across every column an upload can record its category in.
  //
  // Reading the document taxonomy's `required` flag instead got this wrong
  // twice over: it missed requirements the taxonomy has no entry for (the
  // packet's photo slots), and it counted a requirement as outstanding when
  // the file that satisfied it recorded a requirement key rather than a
  // category. Falls back to the taxonomy for a tenant with no packet.
  const stillNeeded = useMemo(() => {
    const items = checklist?.items ?? []
    if (items.length > 0) {
      return items
        .filter((i) => i.is_required && !i.satisfied)
        .map((i) => ({ key: i.key, label: i.label }))
    }
    const present = new Set(
      attachments.filter((a) => !a.is_deleted).map((a) => a.category),
    )
    return categories
      .filter((c) => c.required && !present.has(c.key))
      .map((c) => ({ key: c.key, label: c.label }))
  }, [checklist, categories, attachments])

  // A requirement can name a SUB-category rather than a category, and the
  // picker lists top-level categories only, so index every sub-category by key
  // to resolve a requirement to the parent that IS offered. A top-level key
  // always wins, and a sub key found under two parents resolves to neither
  // (``null``) rather than guessing the first.
  const subcategoryIndex = useMemo(() => {
    const topLevel = new Set(allCategories.map((c) => c.key))
    const index = new Map<
      string,
      { categoryKey: string; categoryLabel: string; subLabel: string } | null
    >()
    for (const c of allCategories) {
      for (const sub of c.subcategories ?? []) {
        if (topLevel.has(sub.key)) continue
        const entry = {
          categoryKey: c.key,
          categoryLabel: c.label,
          subLabel: sub.label,
        }
        const existing = index.get(sub.key)
        if (existing === undefined) index.set(sub.key, entry)
        else if (existing && existing.categoryKey !== c.key)
          index.set(sub.key, null)
      }
    }
    return index
  }, [allCategories])

  // Where each outstanding requirement lives in the picker. A top-level
  // category resolves to itself; a sub-category resolves to its parent and is
  // labelled with the full path, so the banner names somewhere the reviewer
  // can actually go. The labels are tenant-authored and arrive from the API,
  // so no new copy is introduced here.
  const stillNeededDisplay = useMemo(
    () =>
      stillNeeded.map((item) => {
        const sub = subcategoryIndex.get(item.key)
        // Ambiguous: name it as the checklist does, and preselect nothing.
        if (sub === null) {
          return {
            key: item.key,
            label: item.label,
            categoryKey: '',
            subcategoryKey: '',
          }
        }
        return sub
          ? {
              key: item.key,
              label: `${sub.categoryLabel} / ${sub.subLabel}`,
              categoryKey: sub.categoryKey,
              subcategoryKey: item.key,
            }
          : {
              key: item.key,
              label: item.label,
              categoryKey: item.key,
              subcategoryKey: '',
            }
      }),
    [stillNeeded, subcategoryIndex],
  )

  // When the claim is waiting on exactly ONE document, start the form on it.
  // The reviewer opened this dialog to satisfy that requirement - the banner
  // above the field already names it - so asking them to pick it out of a list
  // is asking a question whose answer is on screen. Only when there is exactly
  // one: with several outstanding we would be guessing, and a wrong category
  // files the document where no reviewer looks for it. Never overrides a
  // choice already made, and only pre-selects a category the list actually
  // offers.
  useEffect(() => {
    if (categoryValue) return
    if (stillNeededDisplay.length !== 1) return
    const only = stillNeededDisplay[0]
    if (!categoryOptions.some((o) => o.value === only.categoryKey)) return
    setCategoryValue(only.categoryKey)
    // A requirement that names a sub-category selects both halves: choosing
    // the parent alone would leave the reviewer on a second question whose
    // answer the banner already gave.
    if (only.subcategoryKey) setSubcategoryKey(only.subcategoryKey)
  }, [categoryValue, stillNeededDisplay, categoryOptions])

  const selectedCategory = useMemo(
    () => categories.find((c) => c.key === categoryValue),
    [categories, categoryValue],
  )
  const subcategories = selectedCategory?.subcategories ?? []
  const subRequired = selectedCategory && subcategories.length > 0

  // The claim's resolved checklist items (per-defect required photos/documents),
  // widened with the tenant's wider photo catalogue so an upload can be filed
  // under a category this claim's defect does not itself require (WPS-140 #5).
  // Absent unless the tenant has seeded an evidence-request template, so the
  // selector below self-gates on data presence.
  const checklistItems = useMemo<EvidenceCategoryOption[]>(() => {
    const items = (checklist?.items ?? []).map((i) => ({
      key: i.key,
      label: i.label,
      satisfied: i.satisfied,
    }))
    const seen = new Set(items.map((i) => i.key))
    const extra = (checklist?.all_photo_categories ?? [])
      .filter((c) => !seen.has(c.key))
      .map((c) => ({ key: c.key, label: c.label, satisfied: c.satisfied }))
    return [...items, ...extra]
  }, [checklist])

  // A category that just names the medium ("Photo", "Document") says nothing
  // about what the evidence IS, so it cannot satisfy a checklist item and
  // leaves the file in a catch-all bucket. When the tenant has a real category
  // list, make choosing one mandatory rather than offering a generic tag that
  // files the upload nowhere.
  const isGenericMediaCategory =
    !!selectedCategory && GENERIC_MEDIA_CATEGORY_KEYS.has(categoryValue)

  // A tenant's document taxonomy and its evidence packet are authored
  // separately, so a key often appears in both - the same fact asked twice.
  // Whichever keys a given tenant happens to share, link them rather than
  // making the user restate it; an unlinked upload is what left the checklist
  // showing a requirement as missing after it had been satisfied.
  const autoChecklistKey = useMemo(() => {
    if (!categoryValue) return ''
    const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, '_')
    const target = norm(categoryValue)
    return checklistItems.find((i) => norm(i.key) === target)?.key ?? ''
  }, [categoryValue, checklistItems])

  const requiresChecklistItem =
    isGenericMediaCategory &&
    !subRequired &&
    !autoChecklistKey &&
    checklistItems.length > 0

  useEffect(() => {
    if (autoChecklistKey) setChecklistItemKey(autoChecklistKey)
  }, [autoChecklistKey])

  const categoryChosen =
    !!categoryValue &&
    (!subRequired || !!subcategoryKey) &&
    (!requiresChecklistItem || !!checklistItemKey)

  const uploadFn = async (file: File): Promise<UploadedFileItem> => {
    try {
      const r = await upload.mutateAsync({
        file,
        category: categoryValue,
        subcategory: subRequired ? subcategoryKey : undefined,
        requirementKey: checklistItemKey || undefined,
      })
      return {
        uuid: r.uuid,
        file_name: r.file_name ?? file.name,
        file_url: r.file_url,
        mime_type: r.mime_type,
      }
    } catch (err) {
      toastApiError(err, 'Failed to add evidence')
      throw err
    }
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    const uuid = pendingDelete.uuid
    try {
      await del.mutateAsync({ uuid })
      setAdded((prev) => prev.filter((f) => f.uuid !== uuid))
      setPendingDelete(null)
    } catch {
      // useDeleteAttachment surfaces the error toast; keep the confirm open so
      // the user can retry or cancel rather than silently "succeeding".
    }
  }

  const close = () => {
    setCategoryValue('')
    setSubcategoryKey('')
    setChecklistItemKey('')
    setAdded([])
    setPendingDelete(null)
    onClose()
  }

  const stillNeededBanner =
    stillNeededDisplay.length > 0 ? (
      <div className="flex items-start gap-3 rounded-[10px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-3.5">
        <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-[var(--ink-3)]" />
        <div>
          <p className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
            Still needed
          </p>
          <p className="mt-1 text-[15px] leading-relaxed text-[var(--ink-2)]">
            {stillNeededDisplay.map((c) => c.label).join(', ')}
          </p>
        </div>
      </div>
    ) : null

  const classifier = (
    <>
      {/* Category (required) */}
      <div className="flex flex-col gap-2">
        <Label className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Category <span className="text-[var(--status-error)]">*</span>
        </Label>
        <CreatableSelect
          value={categoryValue}
          onValueChange={(v) => {
            setCategoryValue(v)
            setSubcategoryKey('')
            // Drop any link carried over from the previous category; the
            // effect below re-links it when the new one maps to a requirement.
            setChecklistItemKey('')
          }}
          onCreate={(label) => {
            createCategory.mutate(
              { label },
              {
                onSuccess: (data) => {
                  setSubcategoryKey('')
                  setCategoryValue(data.category.key)
                },
                onError: (err) =>
                  toastApiError(err, 'Failed to create category'),
              },
            )
          }}
          options={categoryOptions}
          placeholder="Select a category"
          maxLength={64}
        />
      </div>

      {/* Sub-category (only when the category has them) */}
      {subRequired ? (
        <div className="flex flex-col gap-2">
          <Label className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
            {selectedCategory?.label} type
          </Label>
          <Select value={subcategoryKey} onValueChange={setSubcategoryKey}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a type" />
            </SelectTrigger>
            <SelectContent>
              {subcategories.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {/* Fulfils checklist item (optional) - only when the claim resolves a
          checklist. Maps the upload to a specific required photo/document so it
          shows satisfied on the Review Checklist (structure photo, slope photo,
          etc.), which coarse categories alone can't express. */}
      {checklistItems.length > 0 && !autoChecklistKey ? (
        <div className="flex flex-col gap-2">
          <Label className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
            {requiresChecklistItem
              ? t('claims.evidenceCategoryLabel', 'Evidence category')
              : t('claims.fulfilsChecklistItem', 'Fulfils checklist item')}
          </Label>
          <Select
            // With a real category required the sentinel row is not rendered,
            // so falling back to it would leave the trigger showing the raw
            // sentinel instead of the placeholder.
            value={
              checklistItemKey || (requiresChecklistItem ? '' : NO_CHECKLIST_ITEM)
            }
            onValueChange={(v: string) =>
              setChecklistItemKey(v === NO_CHECKLIST_ITEM ? '' : v)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  requiresChecklistItem
                    ? t('claims.chooseEvidenceCategory', 'Choose a category')
                    : t(
                        'claims.notLinkedToChecklistItem',
                        'Not linked to a checklist item',
                      )
                }
              />
            </SelectTrigger>
            <SelectContent>
              {/* A generic medium tells a reviewer nothing, so "not linked"
                  is not offered once a real category is required. */}
              {(requiresChecklistItem
                ? checklistItems
                : [NOT_LINKED_OPTION(t), ...checklistItems]
              ).map((item) => (
                <SelectItem key={item.key} value={item.key}>
                  {item.label}
                  {item.satisfied
                    ? t('claims.checklistItemReceivedSuffix', ' (received)')
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[12px] leading-relaxed text-[var(--ink-3)]">
            {requiresChecklistItem
              ? t(
                  'claims.chooseEvidenceCategoryHelp',
                  "Pick what this file shows so it files under the right category and counts towards the claim's checklist.",
                )
              : t(
                  'claims.linkUploadToChecklistHelp',
                  "Link this upload to a required document or photo so it shows on the claim's checklist.",
                )}
          </p>
        </div>
      ) : null}
    </>
  )

  return (
    <>
      <AddEvidenceDialogShell
        title="Add evidence"
        description="Classify the document so reviewers know what it is."
        banner={stillNeededBanner}
        classifier={classifier}
        classifierReady={categoryChosen}
        loading={categoriesLoading}
        error={
          categoriesError
            ? "Couldn't load document categories. Please close and try again."
            : null
        }
        value={added}
        onChange={setAdded}
        uploadFn={uploadFn}
        accept={ACCEPT}
        acceptLabel={humanizeAccept(ACCEPT)}
        multiple
        canRemove
        onRemove={(file) => setPendingDelete(file)}
        notReadyHint="Select a category first"
        onClose={close}
      />

      {pendingDelete ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !del.isPending) setPendingDelete(null)
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete file</DialogTitle>
              <DialogDescription>
                Remove{' '}
                <span className="font-medium text-[var(--ink)]">
                  {pendingDelete.file_name}
                </span>{' '}
                from this claim? The file stays in storage for audit.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingDelete(null)}
                disabled={del.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => void confirmDelete()}
                disabled={del.isPending}
              >
                {del.isPending ? (
                  <>
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  )
}
